import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { 
  BarChart3, 
  TrendingUp, 
  Server, 
  Clock, 
  Activity,
  AlertTriangle,
  RefreshCw
} from 'lucide-react'
import { api } from '@/lib/api'

interface ServerMetrics {
  server_id: string
  server_name: string
  avg_response_time: number
  request_count: number
  error_rate: number
  cpu_usage: number
  memory_usage: number
  uptime: number
  cache_hit_rate?: number
}

interface TimeRange {
  label: string
  value: string
  days: number
}

interface ServerComparisonChartProps {
  timeRange: TimeRange
}

export function ServerComparisonChart({ timeRange }: ServerComparisonChartProps) {
  const [servers, setServers] = useState<ServerMetrics[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedMetric, setSelectedMetric] = useState<'response_time' | 'requests' | 'errors' | 'uptime'>('response_time')

  useEffect(() => {
    fetchServerMetrics()
  }, [timeRange])

  const fetchServerMetrics = async () => {
    try {
      setLoading(true)
      // List servers then fetch metrics for each
      const list = await api.get<{ servers: Array<{ id: string; name: string }> }>('/servers')
      const results = await Promise.all(
        (list?.servers || []).map(async (s) => {
          try {
            const m = await api.get<any>(`/servers/${s.id}/metrics`)
            const metrics = m?.metrics || {}
            return {
              server_id: s.id,
              server_name: s.name,
              avg_response_time: Number(metrics.response_time_avg || 0),
              request_count: Number(metrics.request_count || 0),
              error_rate: Number(metrics.error_rate || 0),
              cpu_usage: Number(metrics.cpu_usage || 0),
              memory_usage: Number(metrics.memory_usage || 0),
              uptime: 100, // Placeholder; not returned as percentage
              cache_hit_rate: Number(metrics.cache_hit_rate || 0),
            } as ServerMetrics
          } catch {
            return {
              server_id: s.id,
              server_name: s.name,
              avg_response_time: 0,
              request_count: 0,
              error_rate: 0,
              cpu_usage: 0,
              memory_usage: 0,
              uptime: 0,
              cache_hit_rate: 0,
            } as ServerMetrics
          }
        })
      )

      setServers(results)
    } catch (error) {
      console.error('Failed to fetch server metrics:', error)
    } finally {
      setLoading(false)
    }
  }

  const getMetricData = () => {
    switch (selectedMetric) {
      case 'response_time':
        return {
          label: 'Average Response Time (ms)',
          data: servers.map(s => s.avg_response_time),
          unit: 'ms',
          color: '#F59E0B',
          inverse: true // Lower is better
        }
      case 'requests':
        return {
          label: 'Total Requests',
          data: servers.map(s => s.request_count),
          unit: '',
          color: '#3B82F6',
          inverse: false
        }
      case 'errors':
        return {
          label: 'Error Rate (%)',
          data: servers.map(s => s.error_rate),
          unit: '%',
          color: '#EF4444',
          inverse: true // Lower is better
        }
      case 'uptime':
        return {
          label: 'Uptime (%)',
          data: servers.map(s => s.uptime),
          unit: '%',
          color: '#10B981',
          inverse: false
        }
      default:
        return {
          label: 'Average Response Time (ms)',
          data: servers.map(s => s.avg_response_time),
          unit: 'ms',
          color: '#F59E0B',
          inverse: true
        }
    }
  }

  const renderBarChart = () => {
    if (servers.length === 0) return null

    const metricData = getMetricData()
    const maxValue = Math.max(...metricData.data)
    const minValue = Math.min(...metricData.data)

    return (
      <div className="space-y-4">
        {servers.map((server, index) => {
          const value = metricData.data[index]
          const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0
          const isGood = metricData.inverse ? value === minValue : value === maxValue
          
          return (
            <div key={server.server_id} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-sm">{server.server_name}</span>
                  {isGood && <Badge variant="outline" className="text-xs">Best</Badge>}
                </div>
                <span className="text-sm font-mono">
                  {selectedMetric === 'requests' ? value.toLocaleString() : value.toFixed(1)}{metricData.unit}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${percentage}%`,
                    backgroundColor: metricData.color
                  }}
                />
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const getPerformanceRating = (server: ServerMetrics) => {
    // Simple scoring algorithm
    const responseScore = Math.max(0, 100 - (server.avg_response_time / 10))
    const errorScore = Math.max(0, 100 - (server.error_rate * 20))
    const uptimeScore = server.uptime
    const resourceScore = Math.max(0, 100 - ((server.cpu_usage + server.memory_usage) / 2))
    
    const totalScore = (responseScore + errorScore + uptimeScore + resourceScore) / 4
    
    if (totalScore >= 90) return { rating: 'Excellent', color: 'text-green-600', bgColor: 'bg-green-100' }
    if (totalScore >= 75) return { rating: 'Good', color: 'text-blue-600', bgColor: 'bg-blue-100' }
    if (totalScore >= 60) return { rating: 'Fair', color: 'text-yellow-600', bgColor: 'bg-yellow-100' }
    return { rating: 'Poor', color: 'text-red-600', bgColor: 'bg-red-100' }
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <BarChart3 className="h-5 w-5" />
            <span>Server Comparison</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-500 mr-2" />
            <span className="text-muted-foreground">Loading server comparison data...</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Comparison Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <BarChart3 className="h-5 w-5" />
            <span>Server Comparison</span>
          </CardTitle>
          <CardDescription>Compare key metrics across all servers for {timeRange.label.toLowerCase()}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Metric Selection */}
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'response_time', label: 'Response Time', icon: Clock },
                { key: 'requests', label: 'Requests', icon: Activity },
                { key: 'errors', label: 'Error Rate', icon: AlertTriangle },
                { key: 'uptime', label: 'Uptime', icon: TrendingUp }
              ].map(({ key, label, icon: Icon }) => (
                <Button
                  key={key}
                  variant={selectedMetric === key ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedMetric(key as any)}
                  className="flex items-center space-x-1"
                >
                  <Icon className="h-4 w-4" />
                  <span>{label}</span>
                </Button>
              ))}
            </div>

            {/* Chart */}
            {renderBarChart()}
          </div>
        </CardContent>
      </Card>

      {/* Performance Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Server className="h-5 w-5" />
            <span>Performance Overview</span>
          </CardTitle>
          <CardDescription>Overall performance rating for each server</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {servers.map((server) => {
              const rating = getPerformanceRating(server)
              
              return (
                <div key={server.server_id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${rating.bgColor}`} />
                    <div>
                      <div className="font-medium">{server.server_name}</div>
                      <div className="text-sm text-muted-foreground">
                        {server.request_count.toLocaleString()} requests
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-medium ${rating.color}`}>
                      {rating.rating}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {server.uptime.toFixed(1)}% uptime
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  )
} 